import { useCallback, useMemo } from 'react';

function useNumberFormatter() {
  const formatter = useMemo(() => new Intl.NumberFormat(navigator.language), []);

  const groupSeparator = useMemo(
    () => formatter.formatToParts(1111111).find((part) => part.type === 'group')?.value ?? ',',
    [formatter]
  );

  const decimalSeparator = useMemo(
    () => formatter.formatToParts(1.1).find((part) => part.type === 'decimal')?.value ?? '.',
    [formatter]
  );

  const parseInteger = useCallback(
    function (raw: string): { intValue: number | null; valid: boolean } {
      const normalized = raw.replaceAll(groupSeparator, '').trim();

      if (normalized === '') return { intValue: null, valid: true };
      if (!/^-?\d+$/.test(normalized)) return { intValue: null, valid: false };

      const parsed = Number(normalized);
      return Number.isSafeInteger(parsed)
        ? { intValue: parsed, valid: true }
        : { intValue: null, valid: false };
    },
    [groupSeparator]
  );

  const parsePercentage = useCallback(
    function (raw: string): { numberValue: number | null; valid: boolean } {
      let normalized = raw.trim();

      if (normalized === '') return { numberValue: null, valid: true };

      // Remove grouping separators
      normalized = normalized.replaceAll(groupSeparator, '');

      // Replace locale decimal with JS decimal
      if (decimalSeparator !== '.') {
        normalized = normalized.replace(decimalSeparator, '.');
      }

      // Allow partial inputs like "-" or "."
      if (/^-?$/.test(normalized) || /^-?\.$/.test(normalized)) {
        return { numberValue: null, valid: false };
      }

      // Validate number (allows decimals)
      if (!/^-?\d*(\.\d*)?$/.test(normalized)) {
        return { numberValue: null, valid: false };
      }

      const parsed = Number(normalized);

      if (!Number.isFinite(parsed)) {
        return { numberValue: null, valid: false };
      }

      const truncated = Math.trunc(parsed);

      return { numberValue: truncated, valid: true };
    },
    [groupSeparator, decimalSeparator]
  );

  const parseCurrency = useCallback(
    function (raw: string): { numberValue: number | null; valid: boolean } {
      let normalized = raw.trim();

      if (normalized === '') return { numberValue: null, valid: true };

      // Remove grouping separators
      normalized = normalized.replaceAll(groupSeparator, '');

      // Replace locale decimal with JS decimal
      if (decimalSeparator !== '.') {
        normalized = normalized.replace(decimalSeparator, '.');
      }

      // Allow partial inputs like "-" or "."
      if (/^-?$/.test(normalized) || /^-?\.$/.test(normalized)) {
        return { numberValue: null, valid: false };
      }

      // Validate number (allows decimals)
      if (!/^-?\d*(\.\d*)?$/.test(normalized)) {
        return { numberValue: null, valid: false };
      }

      const parsed = Number(normalized);

      if (!Number.isFinite(parsed)) {
        return { numberValue: null, valid: false };
      }

      const truncated = Math.trunc(parsed);

      return { numberValue: truncated, valid: true };
    },
    [groupSeparator, decimalSeparator]
  );

  return { formatter, parseInteger, parsePercentage, parseCurrency };
}

export default useNumberFormatter;
